// Self-contained 2D-canvas renderer for the training-player power chart.
// Replaces the G2/@ant-design/plots chart in LayoutTraining: G2 v5 measures
// its container only at mount, its resize re-render is disproportionately slow
// (~11 s of main-thread work, data-independent), and remounting per resize
// frame hangs the page. A canvas redraws the ~2 trends (<=2400 decimated
// points each) in milliseconds and stays crisp at any devicePixelRatio.

export const PLAYER_CHART_COLORS = {
    areaFill: 'rgba(45, 169, 223, 0.35)',
    target: '#2da9df',
    live: '#ff4d4f',
    ftp: '#f5f5f5',
    marker: '#ffd31a',
    grid: '#3a3a3a',
    axis: '#393939',
    label: '#a7a7a7',
}

export function formatTimeTick(value) {
    if (value == null || Number.isNaN(Number(value))) return ''
    const total = Math.max(0, Math.round(Number(value)))
    const minutes = Math.floor(total / 60)
    const seconds = total % 60
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function niceYMax(value) {
    if (value == null || Number.isNaN(Number(value))) return 50
    return Math.max(50, Math.ceil(Number(value) / 50) * 50)
}

// Human-friendly tick step for a range: 1/2/5 × 10^n so the axis labels land
// on round values. Returns the step and the tick values below `range`.
export function axisTicks(range, targetCount = 6) {
    const count = Math.max(1, targetCount)
    const rough = Math.max(1, range) / count
    const pow = Math.pow(10, Math.floor(Math.log10(rough)))
    const step = Math.max(1, [1, 2, 5, 10].find(m => m * pow >= rough) * pow)
    const ticks = []
    for (let v = step; v < range; v += step) ticks.push(v)
    return { step, ticks }
}

function polyline(ctx, data, toX, toY) {
    ctx.beginPath()
    ctx.moveTo(toX(data[0].time), toY(data[0].watts))
    for (let i = 1; i < data.length; i++) ctx.lineTo(toX(data[i].time), toY(data[i].watts))
}

export function renderPlayerChart(canvas, {
    targetData = [], liveDatum = [], elapsedSeconds = 0, ftpWatts = null, duration = 0, yMax,
}) {
    const ctx = canvas.getContext && canvas.getContext('2d')
    if (!ctx) return

    const rect = canvas.getBoundingClientRect()
    const dpr = (typeof window !== 'undefined' && window.devicePixelRatio) || 1
    const w = Math.max(1, Math.round(rect.width))
    const h = Math.max(1, Math.round(rect.height))
    const bw = Math.round(w * dpr)
    const bh = Math.round(h * dpr)
    if (canvas.width !== bw || canvas.height !== bh) {
        canvas.width = bw
        canvas.height = bh
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    ctx.clearRect(0, 0, w, h)

    const pad = { top: 10, right: 14, bottom: 26, left: 46 }
    const plotL = pad.left
    const plotR = w - pad.right
    const plotT = pad.top
    const plotB = h - pad.bottom
    const plotW = Math.max(1, plotR - plotL)
    const plotH = Math.max(1, plotB - plotT)

    const xMax = Math.max(1, duration)
    const yMaxV = yMax != null && yMax > 0 ? yMax : 50
    const toX = v => plotL + (v / xMax) * plotW
    const toY = v => plotB - (v / yMaxV) * plotH

    ctx.font = '11px -apple-system, "Segoe UI", Roboto, sans-serif'

    // Y grid + labels
    const yticks = axisTicks(yMaxV, 5).ticks
    ctx.textAlign = 'right'
    ctx.textBaseline = 'middle'
    for (const t of yticks) {
        const yy = toY(t)
        ctx.strokeStyle = PLAYER_CHART_COLORS.grid
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(plotL, yy)
        ctx.lineTo(plotR, yy)
        ctx.stroke()
        ctx.fillStyle = PLAYER_CHART_COLORS.label
        ctx.fillText(String(Math.round(t)), plotL - 7, yy)
    }

    // X axis line + labels
    const xticks = axisTicks(duration, 8).ticks
    ctx.strokeStyle = PLAYER_CHART_COLORS.axis
    ctx.lineWidth = 1
    ctx.beginPath()
    ctx.moveTo(plotL, plotB)
    ctx.lineTo(plotR, plotB)
    ctx.stroke()
    ctx.textAlign = 'center'
    ctx.textBaseline = 'top'
    for (const t of xticks) {
        const xx = toX(t)
        ctx.beginPath()
        ctx.moveTo(xx, plotB)
        ctx.lineTo(xx, plotB + 4)
        ctx.stroke()
        ctx.fillStyle = PLAYER_CHART_COLORS.label
        ctx.fillText(formatTimeTick(t), xx, plotB + 6)
    }

    // FTP reference line + label
    if (ftpWatts != null && duration > 0) {
        ctx.strokeStyle = PLAYER_CHART_COLORS.ftp
        ctx.lineWidth = 1
        ctx.beginPath()
        ctx.moveTo(plotL, toY(ftpWatts))
        ctx.lineTo(plotR, toY(ftpWatts))
        ctx.stroke()
        ctx.fillStyle = PLAYER_CHART_COLORS.ftp
        ctx.textAlign = 'left'
        ctx.textBaseline = 'bottom'
        ctx.fillText(`FTP ${Math.round(ftpWatts)} W`, plotR - 70, toY(ftpWatts) - 5)
    }

    // Target power: filled stepped area
    if (targetData.length > 1) {
        ctx.fillStyle = PLAYER_CHART_COLORS.areaFill
        ctx.beginPath()
        ctx.moveTo(toX(targetData[0].time), plotB)
        for (const p of targetData) ctx.lineTo(toX(p.time), toY(p.watts))
        ctx.lineTo(toX(targetData[targetData.length - 1].time), plotB)
        ctx.closePath()
        ctx.fill()
    } else if (targetData.length === 1) {
        ctx.fillStyle = PLAYER_CHART_COLORS.target
        ctx.beginPath()
        ctx.arc(toX(targetData[0].time), toY(targetData[0].watts), 3, 0, Math.PI * 2)
        ctx.fill()
    }

    // Target power line
    if (targetData.length > 1) {
        ctx.strokeStyle = PLAYER_CHART_COLORS.target
        ctx.lineWidth = 2
        polyline(ctx, targetData, toX, toY)
        ctx.stroke()
    }

    // Live power line
    if (liveDatum.length > 1) {
        ctx.strokeStyle = PLAYER_CHART_COLORS.live
        ctx.lineWidth = 2
        polyline(ctx, liveDatum, toX, toY)
        ctx.stroke()
    }

    // Current-time marker
    if (duration > 0) {
        const es = Math.max(0, Math.min(elapsedSeconds || 0, xMax))
        const xx = toX(es)
        ctx.strokeStyle = PLAYER_CHART_COLORS.marker
        ctx.lineWidth = 2
        ctx.beginPath()
        ctx.moveTo(xx, plotT)
        ctx.lineTo(xx, plotB)
        ctx.stroke()
    }
}